import { DOCS_FORM_NAMES } from "core/consts";
import { getUnixTime } from "date-fns";
import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledInput,
  ControlledTextarea,
} from "ds/ui";
import { TEXT_AREA_MAX_CHARACTERS_DESCRIPTION } from "dsl/ecosystems/PatientForms/components";
import { FormFieldConfig } from "..";
import {
  AftercareApplicationDateField,
  AftercareApplicationExplanationField,
  AftercareApplicationSubmittedField,
  AftercareCareLevelAppliedDateField,
  AftercareCareLevelAppliedField,
  AftercareCareLevelAvailableField,
  AftercareCareLevelExplanationField,
  CareNecessityField,
  LegalRepresentativeConsentField,
  LegalRepresentativeFirstNameField,
  LegalRepresentativeLastNameField,
  LegalRepresentativeLocationCityField,
  LegalRepresentativeLocationStreetField,
  LegalRepresentativeLocationStreetNumberField,
  LegalRepresentativeLocationZipcodeField,
  PatientConfirmationDateField,
  PatientConfirmationExplanationField,
  PatientConfirmationField,
  PriorTreatmentEndDateField,
  ProviderRequestListRadioGroup,
  SuitableProvidersCountField,
  SuitableProvidersMoreThanTwentyField,
  TransitionalCareEndDateField,
} from "./fields";

export const TransitionalCareFormSections: FormFieldConfig<
  typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE
>["sections"] = [
  // Section 1: Clinic Section
  {
    fields: {
      clinic_ik_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .hospitalIkNumber
            }
            name="clinic_ik_number"
          />
        ),
        position: 2,
      },
      clinic_location_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .hospitalCity
            }
            name="clinic_location_city"
          />
        ),
        position: 6,
      },
      clinic_location_street: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .hospitalStreet
            }
            name="clinic_location_street"
          />
        ),
        position: 3,
      },
      clinic_location_street_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .hospitalStreetNumber
            }
            name="clinic_location_street_number"
          />
        ),
        position: 4,
      },
      clinic_location_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .hospitalZipCode
            }
            name="clinic_location_zipcode"
          />
        ),
        position: 5,
      },
      clinic_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .hospitalName
            }
            name="clinic_name"
          />
        ),
        position: 1,
      },
      handover_to_insurance_date: {
        input: ({ translations }) => (
          <ControlledDatePicker
            label={translations.patientForms.dateField.title}
            name="handover_to_insurance_date"
          />
        ),
        position: 0,
      },
    },
    position: 1,
    title: ({ translations }) =>
      translations.patientForms.transitionalCareForm.contactInformation
        .titleHospital,
  },
  // Section 2: Social Worker Section
  {
    fields: {
      social_worker_email: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .socialWorkerEmail
            }
            name="social_worker_email"
            type="email"
          />
        ),
        position: 5,
      },
      social_worker_fax: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .socialWorkerFax
            }
            name="social_worker_fax"
            type="tel"
          />
        ),
        position: 4,
      },
      social_worker_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .socialWorkerFirstName
            }
            name="social_worker_first_name"
          />
        ),
        position: 2,
      },
      social_worker_last_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .socialWorkerName
            }
            name="social_worker_last_name"
          />
        ),
        position: 1,
      },
      social_worker_telephone: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .socialWorkerPhone
            }
            name="social_worker_telephone"
            type="tel"
          />
        ),
        position: 3,
      },
    },
    position: 2,
    title: ({ translations }) =>
      translations.patientForms.transitionalCareForm.contactInformation
        .titleSocialWorker,
  },
  // Section 3: Patient Contact Section
  {
    fields: {
      patient_date_of_birth: {
        input: ({ translations }) => (
          <ControlledDatePicker
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .patientBirthDate
            }
            maxValue={getUnixTime(new Date())}
            name="patient_date_of_birth"
          />
        ),
        position: 3,
      },
      patient_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .patientFirstName
            }
            name="patient_first_name"
          />
        ),
        position: 2,
      },
      patient_insurance_number: {
        input: ({ translations }) => (
          <ControlledInput
            description={
              translations.patientForms.transitionalCareForm.contactInformation
                .footnoteInsuranceNumber
            }
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .patientInsuranceNumber
            }
            name="patient_insurance_number"
          />
        ),
        position: 4,
      },
      patient_last_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .patientName
            }
            name="patient_last_name"
          />
        ),
        position: 1,
      },
      patient_location_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .patientCity
            }
            name="patient_location_city"
          />
        ),
        position: 8,
      },
      patient_location_house_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .patientStreetNumber
            }
            name="patient_location_house_number"
          />
        ),
        position: 6,
      },
      patient_location_street: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .patientStreet
            }
            name="patient_location_street"
          />
        ),
        position: 5,
      },
      patient_location_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.contactInformation
                .patientZipCode
            }
            name="patient_location_zipcode"
          />
        ),
        position: 7,
      },
    },
    position: 3,
    title: ({ translations }) =>
      translations.patientForms.transitionalCareForm.contactInformation
        .titlePatient,
  },
  // Section 4: Legal Representation Section
  {
    fields: {
      legal_representative_consent_provided: {
        input: LegalRepresentativeConsentField,
        position: 1,
      },
      legal_representative_first_name: {
        input: LegalRepresentativeFirstNameField,
        position: 3,
      },
      legal_representative_last_name: {
        input: LegalRepresentativeLastNameField,
        position: 2,
      },
      legal_representative_location_city: {
        input: LegalRepresentativeLocationCityField,
        position: 7,
      },
      legal_representative_location_street: {
        input: LegalRepresentativeLocationStreetField,
        position: 4,
      },
      legal_representative_location_street_number: {
        input: LegalRepresentativeLocationStreetNumberField,
        position: 5,
      },
      legal_representative_location_zipcode: {
        input: LegalRepresentativeLocationZipcodeField,
        position: 6,
      },
    },
    position: 4,
    title: ({ translations }) =>
      translations.patientForms.transitionalCareForm.contactInformation
        .titleLegalRep,
  },
  // Section 5: Prior Treatment Section
  {
    fields: {
      prior_treatment_end_date: {
        input: PriorTreatmentEndDateField,
        position: 2,
      },
      prior_treatment_hospital_id: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.transitionalCareForm.hospitalStay.id
            }
            name="prior_treatment_hospital_id"
          />
        ),
        position: 3,
      },
      prior_treatment_start_date: {
        input: ({ translations }) => (
          <ControlledDatePicker
            label={
              translations.patientForms.transitionalCareForm.hospitalStay
                .intakeDate
            }
            maxValue={getUnixTime(new Date())}
            name="prior_treatment_start_date"
          />
        ),
        position: 1,
      },
    },
    position: 5,
    title: ({ translations }) =>
      translations.patientForms.transitionalCareForm.hospitalStay
        .titlePriorStay,
  },
  // Section 6: Transitional Care Section
  {
    fields: {
      transitional_care_department_code: {
        input: ({ translations }) => (
          <ControlledInput
            description={
              translations.patientForms.transitionalCareForm.hospitalStay
                .footnoteTwo
            }
            label={
              translations.patientForms.transitionalCareForm.hospitalStay
                .departmentCode
            }
            name="transitional_care_department_code"
          />
        ),
        position: 4,
      },
      transitional_care_end_date: {
        input: TransitionalCareEndDateField,
        position: 2,
      },
      transitional_care_location_id: {
        input: ({ translations }) => (
          <ControlledInput
            description={
              translations.patientForms.transitionalCareForm.hospitalStay
                .footnoteOne
            }
            label={
              translations.patientForms.transitionalCareForm.hospitalStay
                .locationId
            }
            name="transitional_care_location_id"
          />
        ),
        position: 3,
      },
      transitional_care_start_date: {
        input: ({ translations }) => (
          <ControlledDatePicker
            label={
              translations.patientForms.transitionalCareForm.hospitalStay
                .startDateTransitionalCare
            }
            name="transitional_care_start_date"
          />
        ),
        position: 1,
      },
    },
    position: 6,
    title: ({ translations }) =>
      translations.patientForms.transitionalCareForm.hospitalStay
        .titleTransitionalCare,
  },
  // Section PDF-Document Section
  // a) Position 1
  // b) Position 2-5
  // c) Position 6-16
  // d) Position 17
  // e) Position 18
  // f) Position 19
  {
    fields: {
      aftercare_application_date: {
        input: AftercareApplicationDateField,
        position: 10,
      },
      aftercare_application_explanation: {
        input: AftercareApplicationExplanationField,
        position: 11,
      },
      aftercare_application_submitted: {
        input: AftercareApplicationSubmittedField,
        position: 9,
      },
      aftercare_care_level_aftercare_required: {
        input: ({ translations }) => (
          <ControlledCheckbox
            label={
              translations.patientForms.transitionalCareForm.sectionC
                .afterCareRequired
            }
            name="aftercare_care_level_aftercare_required"
          />
        ),
        position: 12,
      },
      aftercare_care_level_applied: {
        input: AftercareCareLevelAppliedField,
        position: 14,
      },
      aftercare_care_level_applied_date: {
        input: AftercareCareLevelAppliedDateField,
        position: 15,
      },
      aftercare_care_level_available: {
        input: AftercareCareLevelAvailableField,
        position: 13,
      },
      aftercare_care_level_explanation: {
        input: AftercareCareLevelExplanationField,
        position: 16,
      },
      aftercare_medical_rehab: {
        input: ({ translations }) => (
          <ControlledCheckbox
            label={
              translations.patientForms.transitionalCareForm.sectionC
                .medicalRehab
            }
            name="aftercare_medical_rehab"
          />
        ),
        position: 8,
      },
      aftercare_mobile: {
        input: ({ translations }) => (
          <ControlledCheckbox
            label={
              translations.patientForms.transitionalCareForm.sectionC.mobileCare
            }
            name="aftercare_mobile"
          />
        ),
        position: 6,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.transitionalCareForm.sectionC.title,
        },
      },
      aftercare_shortterm_care: {
        input: ({ translations }) => (
          <ControlledCheckbox
            label={
              translations.patientForms.transitionalCareForm.sectionC
                .shorttermCare
            }
            name="aftercare_shortterm_care"
          />
        ),
        position: 7,
      },
      care_necessity: {
        input: CareNecessityField,
        position: 19,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.transitionalCareForm.sectionF.title,
        },
      },
      insurance_form_sent_date: {
        input: ({ translations }) => (
          <ControlledDatePicker
            label={
              translations.patientForms.transitionalCareForm.sectionD
                .dateFieldLabel
            }
            maxValue={getUnixTime(new Date())}
            name="insurance_form_sent_date"
          />
        ),
        position: 17,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.transitionalCareForm.sectionD.title,
        },
      },
      internet_portal_used: {
        input: ProviderRequestListRadioGroup,
        position: 18,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.transitionalCareForm.sectionE.title,
        },
      },

      transitional_care_description: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.transitionalCareForm.sectionB
                .textFieldLabel
            }
            maxLength={TEXT_AREA_MAX_CHARACTERS_DESCRIPTION}
            name="transitional_care_description"
          />
        ),
        position: 3,
      },
      transitional_care_patient_confirmation: {
        input: PatientConfirmationField,
        position: 2,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.transitionalCareForm.sectionB.title,
        },
      },
      transitional_care_patient_confirmation_date: {
        input: PatientConfirmationDateField,
        position: 2,
      },
      transitional_care_patient_confirmation_explanation: {
        input: PatientConfirmationExplanationField,
        position: 2,
      },

      transitional_care_start_search_date: {
        input: ({ translations }) => (
          <ControlledDatePicker
            label={
              translations.patientForms.transitionalCareForm.sectionA.dateLabel
            }
            maxValue={getUnixTime(new Date())}
            name="transitional_care_start_search_date"
          />
        ),
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.transitionalCareForm.sectionA.title,
        },
      },
      transitional_care_suitable_providers_count: {
        input: SuitableProvidersCountField,
        position: 5,
      },
      transitional_care_suitable_providers_more_than_twenty: {
        input: SuitableProvidersMoreThanTwentyField,
        position: 4,
      },
    },
    position: 12,
    title: ({ translations }) =>
      translations.patientForms.transitionalCareForm
        .titleDocumentationTransitionalCare,
  },
];
